<template>
  <div class="product-groups" v-loading="loading">
    <div class="product-groups-hint">
      Outras cores, fragrâncias ou versões. Depois de enviar uma, a seleção evita as outras do mesmo
      grupo por alguns meses. Um produto pode estar em vários grupos, como o da linha e o da cor em
      outra linha. Tirar um produto de um grupo o tira para todos os produtos do grupo.
    </div>

    <snowflake-remote-search-select v-if="editable" object-value clear-on-select
      :search-function="productService.search" :find-function="productService.getProductById"
      :label-function="productLabel" placeholder="Adicionar variação" @change="addVariant">
    </snowflake-remote-search-select>

    <div v-for="group in groups" :key="group.key" class="product-group">
      <div class="product-group-header">
        <span class="product-group-label">{{ group.id ? `Grupo ${group.id}` : 'Novo grupo' }}</span>
        <template v-if="editable">
          <el-button type="text" size="mini" :disabled="group.products.length < 2"
            @click="openSplit(group)">Separar</el-button>
          <el-button type="text" size="mini" @click="leave(group)">Sair do grupo</el-button>
        </template>
      </div>

      <el-tag v-for="product in group.products" :key="product.id" :closable="editable"
        disable-transitions @close="removeProduct(group, product)">
        {{ productLabel(product) }}
      </el-tag>

      <snowflake-remote-search-select v-if="editable" object-value clear-on-select
        :search-function="productService.search" :find-function="productService.getProductById"
        :label-function="productLabel" placeholder="Adicionar produto a este grupo"
        @change="(product) => addToGroup(group, product)">
      </snowflake-remote-search-select>
    </div>

    <div v-if="groups.length === 0" class="no-groups">Nenhuma variação</div>

    <el-dialog title="Em qual grupo entrar?" :visible.sync="joining.visible" append-to-body>
      <p>
        {{ productLabel(joining.product) }} está em mais de um grupo. Escolha em quais este produto
        entra.
      </p>
      <el-checkbox-group v-model="joining.selected" class="product-group-choices">
        <el-checkbox v-for="group in joining.groups" :key="group.id" :label="group.id">
          {{ describe(group.products) }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="joining.visible = false">Cancelar</el-button>
        <el-button type="primary" :disabled="joining.selected.length === 0" @click="confirmJoin">
          Entrar
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="Separar em um novo grupo" :visible.sync="splitting.visible" append-to-body>
      <p>
        Este produto sai do grupo e forma um novo só com os produtos marcados, que continuam no
        grupo atual também.
      </p>
      <el-checkbox-group v-model="splitting.selected" class="product-group-choices">
        <el-checkbox v-for="product in splitting.products" :key="product.id" :label="product.id">
          {{ productLabel(product) }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="splitting.visible = false">Cancelar</el-button>
        <el-button type="primary" :disabled="!canSplit" @click="confirmSplit">Separar</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import ProductService from '@/services/stock/ProductService';
import SnowflakeRemoteSearchSelect from '@/components/input/RemoteSearchSelect.vue';

// Edits the variant groups the product is in; the changes are sent by save, when the product is
// saved
export default {
  name: 'product-variant-groups-form',
  components: {
    SnowflakeRemoteSearchSelect,
  },
  props: {
    productId: {
      type: Number,
      required: true,
    },
    editable: Boolean,
  },
  data() {
    return {
      loading: false,
      // Each with the group's other products: { key, id, products }, and no id for a new group
      groups: [],
      newGroups: 0,
      joining: { visible: false, product: null, groups: [], selected: [] },
      splitting: { visible: false, group: null, products: [], selected: [] },
      productService: ProductService,
    };
  },
  computed: {
    // With every product marked, the new group would be the current one
    canSplit() {
      const { products, selected } = this.splitting;

      return selected.length > 0 && selected.length < products.length;
    },
  },
  async created() {
    this.loading = true;

    try {
      this.setGroups(await ProductService.findVariantGroups(this.productId));
    } catch (error) {
      this.$message.error(`Não foi possível carregar as variações: ${error.message}`);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async save() {
      const groups = this.groups.map(group => ({
        id: group.id,
        productIds: group.products.map(product => product.id),
      }));

      this.setGroups(await ProductService.updateVariantGroups(this.productId, groups));
    },
    setGroups(groups) {
      this.groups = groups.map(group => this.groupRow(group));
    },
    groupRow(group) {
      return {
        key: `group-${group.id}`,
        id: group.id,
        products: group.products.filter(product => product.id !== this.productId),
      };
    },
    newGroupRow(products) {
      this.newGroups += 1;

      return { key: `new-${this.newGroups}`, id: null, products };
    },
    // A variant joins the product's group: its group, which the product joins, or a new one
    async addVariant(product) {
      if (!product) return;

      if (product.id === this.productId) {
        this.$message.warning('Este é o próprio produto.');

        return;
      }

      if (this.groups.some(group => group.products.some(other => other.id === product.id))) {
        this.$message.info(`${product.name} já é variação deste produto.`);

        return;
      }

      let groups;

      try {
        groups = await ProductService.findVariantGroups(product.id);
      } catch (error) {
        this.$message.error(
          `Não foi possível carregar as variações de ${product.name}: ${error.message}`);

        return;
      }

      if (groups.length === 0) {
        this.groups.push(this.newGroupRow([{ id: product.id, name: product.name }]));
      } else if (groups.length === 1) {
        this.join(groups[0], product);
      } else {
        // Joining every group could mix lines, as a shade is often grouped with its line and with
        // the same shade in other lines
        this.joining = { visible: true, product, groups, selected: [] };
      }
    },
    confirmJoin() {
      const { product, groups, selected } = this.joining;

      groups
        .filter(group => selected.includes(group.id))
        .forEach(group => this.join(group, product));
      this.joining.visible = false;
    },
    join(group, product) {
      const row = this.groupRow(group);
      const current = this.groups.find(other => other.id === group.id);

      if (current) {
        // Back to a group the product is in, which this product was taken out of
        const listed = current.products.map(other => other.id);
        current.products.push(...row.products.filter(other => !listed.includes(other.id)));
      } else {
        this.groups.push(row);
      }

      const others = row.products.filter(other => other.id !== product.id);

      if (others.length > 0) {
        this.$message.info(
          `Este produto entra no grupo de ${product.name}, com ${this.describe(others)}.`);
      }
    },
    addToGroup(group, product) {
      if (!product || product.id === this.productId) return;
      if (group.products.some(other => other.id === product.id)) return;

      group.products.push({ id: product.id, name: product.name });
    },
    removeProduct(group, product) {
      group.products = group.products.filter(other => other.id !== product.id);
    },
    leave(group) {
      this.groups = this.groups.filter(other => other !== group);
    },
    openSplit(group) {
      this.splitting = { visible: true, group, products: group.products, selected: [] };
    },
    confirmSplit() {
      const { group, products, selected } = this.splitting;

      this.leave(group);
      this.groups.push(this.newGroupRow(products.filter(product => selected.includes(product.id))));
      this.splitting.visible = false;
    },
    describe(products) {
      return products.map(product => this.productLabel(product)).join(', ');
    },
    // A product deleted since it joined the group has no name
    productLabel(product) {
      if (!product) return '';

      return product.name ? `${product.name} (#${product.id})` : `#${product.id} (excluído)`;
    },
  },
};
</script>

<style lang="scss" scoped>
div.product-groups {
  text-align: left;
  line-height: normal;
}

div.product-groups-hint {
  margin-bottom: 8px;
  font-size: 12px;
  color: #5e7382;
}

div.product-group {
  border: #c1c1c1 dashed 1.5px;
  border-radius: 4px;
  margin-top: 8px;
  padding: 8px 12px;

  .el-tag {
    margin: 4px;
  }
}

div.product-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

span.product-group-label {
  font-weight: 600;
  color: #2c3e50;
}

div.no-groups {
  margin-top: 8px;
  font-size: 12px;
  color: #5e7382;
}

.product-group-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .el-checkbox {
    margin-right: 0;
    white-space: normal;
  }
}
</style>
